import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import axiosInstance from "../config/axiosInstance";
import API from "../config/config";
import { toast } from "react-toastify";

const DRIVER_STATUS_OPTIONS = [
  { value: "Pending", label: "Pending" },
  { value: "InProgress", label: "In Progress" },
  { value: "Revisit", label: "In Revisit" },
  { value: "Verified", label: "Verified" },
  { value: "Onboarded", label: "Onboarded" },
  { value: "Rejected", label: "Rejected" },
];

function validatePassword(password, options = {}) {
  const { minLength = 8, minLowercase = 1, minUppercase = 1, minNumbers = 1, minSymbols = 1 } = options;
  const result = { valid: true, errors: [] };
  if (!password || password.length < minLength) {
    result.valid = false;
    result.errors.push(`Password must be at least ${minLength} characters.`);
  }
  if ((password.match(/[a-z]/g) || []).length < minLowercase) { result.valid = false; result.errors.push("At least one lowercase letter."); }
  if ((password.match(/[A-Z]/g) || []).length < minUppercase) { result.valid = false; result.errors.push("At least one uppercase letter."); }
  if ((password.match(/[0-9]/g) || []).length < minNumbers) { result.valid = false; result.errors.push("At least one number."); }
  if ((password.match(/[^A-Za-z0-9]/g) || []).length < minSymbols) { result.valid = false; result.errors.push("At least one symbol."); }
  return result;
}

const defaultModuleActions = () => ({ view: true, edit: false, delete: false });

const AddNewEmployee = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    fullName: "",
    emailId: "",
    password: "",
    phone: "",
    city: "NA",
  });
  const [assignModule, setAssignModule] = useState({
    driver: {
      enabled: false,
      fullAccess: false,
      phoneEdit: false,
      hidePhoneNumber: false,
      drivingChoicesStatus: false,
      drivingChoicesEditDetails: false,
      quickUploadAccess: false,
      addDriverAccess: false,
      allowedStatuses: [],
      actions: defaultModuleActions(),
    },
    rides: { enabled: false, fullAccess: true, actions: defaultModuleActions() },
    taxesCommission: { enabled: false, actions: defaultModuleActions() },
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;
    if (name === "phone") newValue = value.replace(/\D/g, "").slice(0, 10);
    setFormData((prev) => ({ ...prev, [name]: newValue }));
  };

  const setDriverStatus = (status, checked) => {
    setAssignModule((prev) => ({
      ...prev,
      driver: {
        ...prev.driver,
        allowedStatuses: checked
          ? [...(prev.driver.allowedStatuses || []), status]
          : (prev.driver.allowedStatuses || []).filter((s) => s !== status),
      },
    }));
  };

  const setModuleAction = (moduleKey, action, value) => {
    setAssignModule((prev) => ({
      ...prev,
      [moduleKey]: {
        ...prev[moduleKey],
        actions: { ...prev[moduleKey].actions, [action]: value },
      },
    }));
  };

  const setSelectAll = (moduleKey, checked) => {
    setAssignModule((prev) => ({
      ...prev,
      [moduleKey]: {
        ...prev[moduleKey],
        actions: { view: checked, edit: checked, delete: checked },
      },
    }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.fullName?.trim()) newErrors.fullName = "Name is required.";
    if (!formData.emailId?.trim()) newErrors.emailId = "Email is required.";
    else if (!formData.emailId.trim().toLowerCase().endsWith("@udrivo.in")) newErrors.emailId = "Email must be an @udrivo.in address.";
    if (!formData.phone?.trim()) newErrors.phone = "Phone is required.";
    else if (!/^[0-9]{10}$/.test(formData.phone)) newErrors.phone = "Phone must be 10 digits.";
    if (!formData.password) newErrors.password = "Password is required.";
    else {
      const pw = validatePassword(formData.password, { minLength: 8, minLowercase: 1, minUppercase: 1, minNumbers: 1, minSymbols: 1 });
      if (!pw.valid) newErrors.password = pw.errors?.join(" ") || "Invalid password.";
    }
    const hasModule = assignModule.driver?.enabled || assignModule.rides?.enabled || assignModule.taxesCommission?.enabled;
    if (!hasModule) newErrors.assignModule = "Enable at least one module (Driver, Rides, or Tax & Commission).";
    if (
      assignModule.driver?.enabled &&
      !(assignModule.driver.allowedStatuses?.length)
    ) {
      newErrors.allowedStatuses = "Select at least one allowed driver status when the Driver module is enabled.";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await axiosInstance.post(API.ADMIN.EMPLOYEE_NEW_CREATE, {
        fullName: formData.fullName.trim(),
        emailId: formData.emailId.trim().toLowerCase(),
        password: formData.password,
        phone: formData.phone.trim(),
        city: "NA",
        assignModule: {
          // driver: {
          //   enabled: assignModule.driver.enabled,
          //   fullAccess: !!assignModule.driver.fullAccess,
          //   phoneEdit: assignModule.driver.phoneEdit,
          //   dlStatus: assignModule.driver.dlStatus,
          //   aadhaarStatus: assignModule.driver.aadhaarStatus,
          //   panStatus: assignModule.driver.panStatus,
          //   dlEditDetails: !!assignModule.driver.dlEditDetails,
          //   dlEditStatus: !!assignModule.driver.dlEditStatus,
          //   aadhaarEditDetails: !!assignModule.driver.aadhaarEditDetails,
          //   aadhaarEditStatus: !!assignModule.driver.aadhaarEditStatus,
          //   panEditDetails: !!assignModule.driver.panEditDetails,
          //   panEditStatus: !!assignModule.driver.panEditStatus,
          //   drivingChoicesStatus: !!assignModule.driver.drivingChoicesStatus,
          //   drivingChoicesEditDetails: !!assignModule.driver.drivingChoicesEditDetails,
          //   allowedStatuses: assignModule.driver.allowedStatuses || [],
          //   actions: assignModule.driver.actions,
          // },
          driver: {
            enabled: assignModule.driver.enabled,
            fullAccess: !!assignModule.driver.fullAccess,
            phoneEdit: !!assignModule.driver.phoneEdit,
            hidePhoneNumber: !!assignModule.driver.hidePhoneNumber,
            dlStatus: !!assignModule.driver.dlStatus,
            dlEditDetails: !!assignModule.driver.dlEditDetails,
            dlEditStatus: !!assignModule.driver.dlEditStatus,
            aadhaarStatus: !!assignModule.driver.aadhaarStatus,
            aadhaarEditDetails: !!assignModule.driver.aadhaarEditDetails,
            aadhaarEditStatus: !!assignModule.driver.aadhaarEditStatus,
            panStatus: !!assignModule.driver.panStatus,
            panEditDetails: !!assignModule.driver.panEditDetails,
            panEditStatus: !!assignModule.driver.panEditStatus,
            drivingChoicesStatus: !!assignModule.driver.drivingChoicesStatus,
            drivingChoicesEditDetails: !!assignModule.driver.drivingChoicesEditDetails,
            drivingChoicesEditStatus: !!assignModule.driver.drivingChoicesEditStatus,
            quickUploadAccess: !!assignModule.driver.quickUploadAccess,
            addDriverAccess: !!assignModule.driver.addDriverAccess,
            allowedStatuses: assignModule.driver.allowedStatuses || [],
            actions: assignModule.driver.actions,
          },
          rides: {
            enabled: assignModule.rides.enabled,
            fullAccess: assignModule.rides.fullAccess !== false,
            actions: assignModule.rides.actions,
          },
          taxesCommission: {
            enabled: assignModule.taxesCommission.enabled,
            actions: assignModule.taxesCommission.actions,
          },
        },
      });
      toast.success("New employee created successfully.");
      navigate("/employees/new");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to create employee.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-6 py-8 bg-gray-100 min-h-screen">
      <div className="max-w-3xl mx-auto bg-white rounded-xl shadow-lg p-8">
        <h2 className="text-2xl font-bold text-gray-800 mb-6 border-b pb-4">Add New Employee</h2>
        <p className="text-sm text-gray-600 mb-6">Email must be an @udrivo.in address. Configure module access below.</p>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
              <input
                type="text"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                className="w-full px-4 py-2 border rounded-lg"
                placeholder="Full name"
              />
              {errors.fullName && <p className="text-red-500 text-xs mt-1">{errors.fullName}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email ID * (@udrivo.in)</label>
              <input
                type="email"
                name="emailId"
                value={formData.emailId}
                onChange={handleChange}
                className="w-full px-4 py-2 border rounded-lg"
                placeholder="user@udrivo.in"
              />
              {errors.emailId && <p className="text-red-500 text-xs mt-1">{errors.emailId}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className="w-full px-4 py-2 pr-10 border rounded-lg"
                  placeholder="Strong password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-gray-500 hover:text-gray-700 focus:outline-none"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <FaEyeSlash className="w-4 h-4" /> : <FaEye className="w-4 h-4" />}
                </button>
              </div>
              {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full px-4 py-2 border rounded-lg"
                placeholder="10 digits"
                maxLength={10}
              />
              {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
            </div>
            {/* <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                className="w-full px-4 py-2 border rounded-lg"
                placeholder="NA or city name"
              />
            </div> */}
          </div>

{errors.assignModule && <p className="text-red-500 text-sm">{errors.assignModule}</p>}

          {/* Assign Module: Driver */}
          <div className="border rounded-lg p-4 bg-gray-50">
            <label className="flex items-center gap-2 font-medium text-gray-800 mb-3">
              <input
                type="checkbox"
                checked={assignModule.driver.enabled}
                onChange={(e) => setAssignModule((p) => ({ ...p, driver: { ...p.driver, enabled: e.target.checked } }))}
              />
              Enable Driver module
            </label>
            {assignModule.driver.enabled && (
              <div className="ml-6 space-y-3">
                <div className="flex items-center gap-4">
                  <span className="text-sm font-medium text-gray-700">Access type:</span>
                  <label className="inline-flex items-center gap-1">
                    <input
                      type="radio"
                      name="driverAccess"
                      checked={assignModule.driver.fullAccess}
                      onChange={() => setAssignModule((p) => ({ ...p, driver: { ...p.driver, fullAccess: true } }))}
                    />
                    <span className="text-sm">Full Access (all drivers, like Super Admin)</span>
                  </label>
                  <label className="inline-flex items-center gap-1">
                    <input
                      type="radio"
                      name="driverAccess"
                      checked={!assignModule.driver.fullAccess}
                      onChange={() => setAssignModule((p) => ({ ...p, driver: { ...p.driver, fullAccess: false } }))}
                    />
                    <span className="text-sm">Limited Access</span>
                  </label>
                </div>
                {assignModule.driver.fullAccess && (
                  <p className="text-xs text-gray-500">
                    Full Access users see all drivers matching the selected allowed statuses, without assignment restrictions.
                  </p>
                )}
                <div>
                  <span className="text-sm text-gray-700">Quick Upload: </span>
                  <select
                    value={assignModule.driver.quickUploadAccess ? "yes" : "no"}
                    onChange={(e) => setAssignModule((p) => ({ ...p, driver: { ...p.driver, quickUploadAccess: e.target.value === "yes" } }))}
                    className="ml-2 px-2 py-1 border rounded"
                  >
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                  </select>
                </div>
                <div>
                  <span className="text-sm text-gray-700">Add Driver: </span>
                  <select
                    value={assignModule.driver.addDriverAccess ? "yes" : "no"}
                    onChange={(e) => setAssignModule((p) => ({ ...p, driver: { ...p.driver, addDriverAccess: e.target.value === "yes" } }))}
                    className="ml-2 px-2 py-1 border rounded"
                  >
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                  </select>
                </div>
                <div>
                  <span className="text-sm text-gray-700">Phone number edit: </span>
                  <select
                    value={assignModule.driver.phoneEdit ? "yes" : "no"}
                    onChange={(e) => setAssignModule((p) => ({ ...p, driver: { ...p.driver, phoneEdit: e.target.value === "yes" } }))}
                    className="ml-2 px-2 py-1 border rounded"
                  >
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                  </select>
                </div>
                <div>
                  <span className="text-sm text-gray-700">Hide the phone number: </span>
                  <select
                    value={assignModule.driver.hidePhoneNumber ? "yes" : "no"}
                    onChange={(e) => setAssignModule((p) => ({ ...p, driver: { ...p.driver, hidePhoneNumber: e.target.value === "yes" } }))}
                    className="ml-2 px-2 py-1 border rounded"
                  >
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                  </select>
                </div>
                <p className="text-xs text-gray-500 mb-1">Section visibility and Edit details / Edit status apply on the driver detail page only.</p>
                <div>
                  <span className="text-sm text-gray-700">DL Section: </span>
                  <select
                    value={assignModule.driver.dlStatus ? "yes" : "no"}
                    onChange={(e) => setAssignModule((p) => ({ ...p, driver: { ...p.driver, dlStatus: e.target.value === "yes" } }))}
                    className="ml-2 px-2 py-1 border rounded"
                  >
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                  </select>
                  {assignModule.driver.dlStatus && (
                    <div className="ml-6 mt-2 flex gap-4">
                      <label className="inline-flex items-center gap-1 text-sm">
                        <input type="checkbox" checked={!!assignModule.driver.dlEditDetails} onChange={(e) => setAssignModule((p) => ({ ...p, driver: { ...p.driver, dlEditDetails: e.target.checked } }))} />
                        Edit details
                      </label>
                      <label className="inline-flex items-center gap-1 text-sm">
                        <input type="checkbox" checked={!!assignModule.driver.dlEditStatus} onChange={(e) => setAssignModule((p) => ({ ...p, driver: { ...p.driver, dlEditStatus: e.target.checked } }))} />
                        Edit status
                      </label>
                    </div>
                  )}
                </div>
                <div>
                  <span className="text-sm text-gray-700">Aadhaar Section: </span>
                  <select
                    value={assignModule.driver.aadhaarStatus ? "yes" : "no"}
                    onChange={(e) => setAssignModule((p) => ({ ...p, driver: { ...p.driver, aadhaarStatus: e.target.value === "yes" } }))}
                    className="ml-2 px-2 py-1 border rounded"
                  >
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                  </select>
                  {assignModule.driver.aadhaarStatus && (
                    <div className="ml-6 mt-2 flex gap-4">
                      <label className="inline-flex items-center gap-1 text-sm">
                        <input type="checkbox" checked={!!assignModule.driver.aadhaarEditDetails} onChange={(e) => setAssignModule((p) => ({ ...p, driver: { ...p.driver, aadhaarEditDetails: e.target.checked } }))} />
                        Edit details
                      </label>
                      <label className="inline-flex items-center gap-1 text-sm">
                        <input type="checkbox" checked={!!assignModule.driver.aadhaarEditStatus} onChange={(e) => setAssignModule((p) => ({ ...p, driver: { ...p.driver, aadhaarEditStatus: e.target.checked } }))} />
                        Edit status
                      </label>
                    </div>
                  )}
                </div>
                <div>
                  <span className="text-sm text-gray-700">PAN Section: </span>
                  <select
                    value={assignModule.driver.panStatus ? "yes" : "no"}
                    onChange={(e) => setAssignModule((p) => ({ ...p, driver: { ...p.driver, panStatus: e.target.value === "yes" } }))}
                    className="ml-2 px-2 py-1 border rounded"
                  >
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                  </select>
                  {assignModule.driver.panStatus && (
                    <div className="ml-6 mt-2 flex gap-4">
                      <label className="inline-flex items-center gap-1 text-sm">
                        <input type="checkbox" checked={!!assignModule.driver.panEditDetails} onChange={(e) => setAssignModule((p) => ({ ...p, driver: { ...p.driver, panEditDetails: e.target.checked } }))} />
                        Edit details
                      </label>
                      <label className="inline-flex items-center gap-1 text-sm">
                        <input type="checkbox" checked={!!assignModule.driver.panEditStatus} onChange={(e) => setAssignModule((p) => ({ ...p, driver: { ...p.driver, panEditStatus: e.target.checked } }))} />
                        Edit status
                      </label>
                    </div>
                  )}
                </div>
                <div>
                  <span className="text-sm text-gray-700">Driving Choices Section: </span>
                  <select
                    value={assignModule.driver.drivingChoicesStatus ? "yes" : "no"}
                    onChange={(e) => setAssignModule((p) => ({ ...p, driver: { ...p.driver, drivingChoicesStatus: e.target.value === "yes" } }))}
                    className="ml-2 px-2 py-1 border rounded"
                  >
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                  </select>
                  {assignModule.driver.drivingChoicesStatus && (
                    <div className="ml-6 mt-2 flex gap-4">
                      <label className="inline-flex items-center gap-1 text-sm">
                        <input type="checkbox" checked={!!assignModule.driver.drivingChoicesEditDetails} onChange={(e) => setAssignModule((p) => ({ ...p, driver: { ...p.driver, drivingChoicesEditDetails: e.target.checked } }))} />
                        Edit details
                      </label>
                    </div>
                  )}
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-700 block mb-2">Allowed driver statuses (multi-select):</span>
                  <p className="text-xs text-gray-500 mb-2">
                    Select at least one status. The employee will not see any drivers until at least one status is selected.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {DRIVER_STATUS_OPTIONS.map((opt) => (
                      <label key={opt.value} className="flex items-center gap-1 text-sm">
                        <input
                          type="checkbox"
                          checked={(assignModule.driver.allowedStatuses || []).includes(opt.value)}
                          onChange={(e) => setDriverStatus(opt.value, e.target.checked)}
                        />
                        {opt.label}
                      </label>
                    ))}
                  </div>
                  {errors.allowedStatuses && (
                    <p className="text-red-500 text-xs mt-1">{errors.allowedStatuses}</p>
                  )}
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-700 block mb-2">Actions (Drivers list page: View / Edit / Delete buttons):</span>
                  <label className="inline-flex items-center mr-4">
                    <input
                      type="checkbox"
                      checked={assignModule.driver.actions.view}
                      onChange={(e) => setModuleAction("driver", "view", e.target.checked)}
                    />
                    <span className="ml-1">View</span>
                  </label>
                  <label className="inline-flex items-center mr-4">
                    <input
                      type="checkbox"
                      checked={assignModule.driver.actions.edit}
                      onChange={(e) => setModuleAction("driver", "edit", e.target.checked)}
                    />
                    <span className="ml-1">Edit</span>
                  </label>
                  <label className="inline-flex items-center mr-4">
                    <input
                      type="checkbox"
                      checked={assignModule.driver.actions.delete}
                      onChange={(e) => setModuleAction("driver", "delete", e.target.checked)}
                    />
                    <span className="ml-1">Delete</span>
                  </label>
                  <label className="inline-flex items-center">
                    <input
                      type="checkbox"
                      checked={assignModule.driver.actions.view && assignModule.driver.actions.edit && assignModule.driver.actions.delete}
                      onChange={(e) => setSelectAll("driver", e.target.checked)}
                    />
                    <span className="ml-1">Select all</span>
                  </label>
                </div>
              </div>
            )}
          </div>

          {/* Rides module */}
          <div className="border rounded-lg p-4 bg-gray-50">
            <label className="flex items-center gap-2 font-medium text-gray-800 mb-3">
              <input
                type="checkbox"
                checked={assignModule.rides.enabled}
                onChange={(e) => setAssignModule((p) => ({ ...p, rides: { ...p.rides, enabled: e.target.checked } }))}
              />
              Rides module
            </label>
            {assignModule.rides.enabled && (
              <div className="ml-6">
                <span className="text-sm font-medium text-gray-700 block mb-2">Access:</span>
                <label className="inline-flex items-center mr-4">
                  <input type="radio" name="ridesAccess" checked={assignModule.rides.fullAccess !== false} onChange={() => setAssignModule((p) => ({ ...p, rides: { ...p.rides, fullAccess: true } }))} />
                  <span className="ml-1">Full Access</span>
                </label>
                <label className="inline-flex items-center mr-4">
                  <input type="radio" name="ridesAccess" checked={assignModule.rides.fullAccess === false} onChange={() => setAssignModule((p) => ({ ...p, rides: { ...p.rides, fullAccess: false } }))} />
                  <span className="ml-1">Limited Access</span>
                </label>
                <span className="text-sm font-medium text-gray-700 block mb-2 mt-2">Actions:</span>
                <label className="inline-flex items-center mr-4">
                  <input type="checkbox" checked={assignModule.rides.actions.view} onChange={(e) => setModuleAction("rides", "view", e.target.checked)} />
                  <span className="ml-1">View</span>
                </label>
                <label className="inline-flex items-center mr-4">
                  <input type="checkbox" checked={assignModule.rides.actions.edit} onChange={(e) => setModuleAction("rides", "edit", e.target.checked)} />
                  <span className="ml-1">Edit</span>
                </label>
                <label className="inline-flex items-center mr-4">
                  <input type="checkbox" checked={assignModule.rides.actions.delete} onChange={(e) => setModuleAction("rides", "delete", e.target.checked)} />
                  <span className="ml-1">Delete</span>
                </label>
                <label className="inline-flex items-center">
                  <input type="checkbox" checked={assignModule.rides.actions.view && assignModule.rides.actions.edit && assignModule.rides.actions.delete} onChange={(e) => setSelectAll("rides", e.target.checked)} />
                  <span className="ml-1">Select all</span>
                </label>
              </div>
            )}
          </div>

          {/* Tax & Commission module */}
          <div className="border rounded-lg p-4 bg-gray-50">
            <label className="flex items-center gap-2 font-medium text-gray-800 mb-3">
              <input
                type="checkbox"
                checked={assignModule.taxesCommission.enabled}
                onChange={(e) => setAssignModule((p) => ({ ...p, taxesCommission: { ...p.taxesCommission, enabled: e.target.checked } }))}
              />
              Tax & Commission module
            </label>
            {assignModule.taxesCommission.enabled && (
              <div className="ml-6">
                <span className="text-sm font-medium text-gray-700 block mb-2">Actions:</span>
                <label className="inline-flex items-center mr-4">
                  <input type="checkbox" checked={assignModule.taxesCommission.actions.view} onChange={(e) => setModuleAction("taxesCommission", "view", e.target.checked)} />
                  <span className="ml-1">View</span>
                </label>
                <label className="inline-flex items-center mr-4">
                  <input type="checkbox" checked={assignModule.taxesCommission.actions.edit} onChange={(e) => setModuleAction("taxesCommission", "edit", e.target.checked)} />
                  <span className="ml-1">Edit</span>
                </label>
                <label className="inline-flex items-center mr-4">
                  <input type="checkbox" checked={assignModule.taxesCommission.actions.delete} onChange={(e) => setModuleAction("taxesCommission", "delete", e.target.checked)} />
                  <span className="ml-1">Delete</span>
                </label>
                <label className="inline-flex items-center">
                  <input type="checkbox" checked={assignModule.taxesCommission.actions.view && assignModule.taxesCommission.actions.edit && assignModule.taxesCommission.actions.delete} onChange={(e) => setSelectAll("taxesCommission", e.target.checked)} />
                  <span className="ml-1">Select all</span>
                </label>
              </div>
            )}
          </div>

          <div className="flex gap-3 pt-4">
            <button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg disabled:opacity-50">
              {loading ? "Creating..." : "Create New Employee"}
            </button>
            <button type="button" onClick={() => navigate("/employees/new")} className="px-6 py-2 border rounded-lg text-gray-700 hover:bg-gray-50">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddNewEmployee;
